import { Advertisement } from "@cryptors/api-client";
import { Box } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import type { CrudFilter } from "@refinedev/core";
import { IResourceComponentsProps } from "@refinedev/core";
import { List, EditButton, ShowButton, useDataGrid } from "@refinedev/mui";
import React, { useMemo } from "react";

import { DeleteIconButton } from "@/components/forms/DeleteIconButton";
import { SearchField } from "@/components/forms/SearchField";

const buildSearchFilters = (q: string): CrudFilter[] => {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const orValue: CrudFilter[] = [
    { field: "title", operator: "containss", value: trimmed },
    { field: "url", operator: "containss", value: trimmed },
  ];
  if (/^\d+$/.test(trimmed)) {
    orValue.push({ field: "id", operator: "eq", value: Number(trimmed) });
  }
  return [{ operator: "or", value: orValue }];
};

export const AdvertisementList: React.FC<IResourceComponentsProps> = () => {
  const { dataGridProps, setFilters } = useDataGrid<Advertisement>({
    resource: "advertisements",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "id", order: "desc" }] },
  });

  const columns = useMemo<GridColDef<Advertisement>[]>(
    () => [
      {
        field: "title",
        flex: 1,
        headerName: "Titre",
        minWidth: 200,
      },
      {
        field: "url",
        flex: 1,
        headerName: "Url",
        minWidth: 200,
      },
      {
        field: "image.url",
        headerName: "Image",
        sortable: false,
        minWidth: 50,
        valueGetter: (_value, row) => row.image?.url,
        renderCell: function render({ value }) {
          return <img src={value} alt="Badge" width="50" />;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteIconButton recordItemId={row.id} />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 160,
      },
    ],
    [],
  );

  return (
    <List>
      <Box sx={{ mb: 2 }}>
        <SearchField
          placeholder="Recherche (titre, url, id)"
          buildFilters={buildSearchFilters}
          onSearch={setFilters}
        />
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </List>
  );
};
